import * as React from 'react';
import type { Control, FieldValues, Validate } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import Help from '@mui/icons-material/Help';
import type { SelectChangeEvent } from '@mui/material';
import { FormControl } from '@mui/material';
import { Box } from '@mui/material';

import type { SelectOption } from 'ui';
import { Container, ContainerItem, Error, InputLabel, Select, Text, Tooltip } from 'ui';
import { errorMessages } from 'util/forms.util';

interface ValidationRule<T> {
  message: string;
  value: T;
}

interface Props {
  label: string;
  name: string;
  options: SelectOption[];
  noLabel?: boolean;
  control?: Control;
  multiSelect?: boolean;
  defaultValue?: number | string | string[];
  readOnly?: boolean;
  viewOnly?: boolean;
  rules?: {
    required?: ValidationRule<boolean>;
    maxLength?: ValidationRule<number>;
    minLength?: ValidationRule<number>;
    max?: ValidationRule<number>;
    min?: ValidationRule<number>;
    pattern?: ValidationRule<RegExp>;
    validate?:
      | Validate<string | number, FieldValues>
      | Record<string, Validate<string | number, FieldValues>>;
  };
  tooltip?: string;
  validateError?: string;
  dataCy?: string;
  additionalOnChange?: (e: SelectChangeEvent<any>) => void;
  background?: 'white' | 'grey' | 'dark-grey';
  onBlur?: () => void;
  emptyLabel?: string;
  listItemsWidth?: string;
  addTag?: string;
  useChip?: boolean;
  value?: string | string[];
  placeholder?: string;
  headerComponent?: boolean;
  disableSelected?: boolean;
  darkLabel?: boolean;
  disablePortal?: boolean;
  labelDetails?: React.ReactNode;
}

export const InputSelect = (props: Props) => {
  return (
    <FormControl fullWidth data-cy={props.dataCy}>
      {!props.noLabel ? (
        <Container align="center" padding={0} width="100%">
          <ContainerItem flex align="center" padding={0} margin={'0 0 6px 0'}>
            <InputLabel
              dataCy={'select-label'}
              label={props.label}
              name={props.name}
              required={props.viewOnly ? false : props.rules?.required?.value}
              readOnly={props.readOnly}
              rules={props.rules}
              viewOnly={props.viewOnly}
              darkLabel={props.darkLabel}
            />
            {props.labelDetails}
          </ContainerItem>

          {props.tooltip ? (
            <ContainerItem padding={0} align="center" flex margin="-3px 0 4px 2px">
              <Tooltip variant="text" title={props.tooltip} placement="right">
                <Help color="primary" />
              </Tooltip>
            </ContainerItem>
          ) : null}
        </Container>
      ) : null}
      {props.viewOnly &&
      !props.name.includes('[') && // nested objects dont work here so exclude them (i.e. props.name === 'locations[0].state')
      (props.control?._formValues[props.name] === undefined ||
        props.control?._formValues[props.name] === '' ||
        props.control?._formValues[props.name] === '--') ? (
        <Box sx={{ p: '0.25rem 0' }}>
          <Text
            sx={{
              fontFamily: 'Lato',
              fontStyle: 'normal',
              fontWeight: 'normal',
              fontSize: '16px',
              lineHeight: '20px'
            }}
            content="--"
          />
        </Box>
      ) : (
        <Controller
          control={props.control}
          name={props.name}
          rules={props.rules}
          defaultValue={props.defaultValue}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <Select
                  headerComponent={props.headerComponent ? true : false}
                  additionalOnChange={props.additionalOnChange}
                  background={props.background}
                  dataCy="select-dropdown"
                  label={props.label}
                  multiSelect={props.multiSelect}
                  name={props.name}
                  onBlur={() => {
                    if (props.onBlur) {
                      props.onBlur();
                    }

                    onBlur();
                  }}
                  onChange={onChange}
                  options={props.options}
                  readOnly={props.readOnly}
                  viewOnly={props.viewOnly}
                  value={value !== undefined && value !== 'create-section' ? value : props.value}
                  emptyLabel={props.emptyLabel}
                  listItemsWidth={props.listItemsWidth}
                  addTag={props.addTag ?? ''}
                  useChip={props.useChip}
                  placeholder={props.placeholder}
                  disableSelected={props.disableSelected}
                  disablePortal={props.disablePortal}
                />

                <Error
                  content={error ? `* ${props.label} ${error.message || errorMessages()[error.type]}` : ''}
                />
              </>
            );
          }}
        />
      )}
    </FormControl>
  );
};
